import classNames from 'classnames';
import type { CSSProperties } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import debug from 'utils/debug';
import { isElectronEnv } from 'utils/electron';
import styles from './index.module.scss';

export interface HeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  alignToContainer?: boolean;
  children?: React.ReactNode;
  className?: string;
  colorVariant?: 'default' | 'white' | 'transparent' | 'lightGray' | 'darkGray' | 'flat';
  height?: 'default' | 'short' | 'medium' | 'tall' | 'none';
  isDivider?: boolean;
  sticky?: boolean;
  withRows?: boolean;
}

export const Header: React.FC<HeaderProps> = props => {
  const {
    children,
    className,
    withRows,
    alignToContainer,
    sticky,
    height = 'default',
    isDivider,
    colorVariant = 'default',
    ...rest
  } = props;
  const containerClass = classNames(
    styles.header,
    styles[`height-${height}`],
    styles[`color-variant-${colorVariant}`],
    {
      [styles.sticky]: sticky,
      [styles.inElectron]: isElectronEnv(),
      [styles.withRows]: withRows,
      [styles.alignToContainer]: alignToContainer,
      [styles.borderTop]: isDivider,
    },
    className
  );
  return (
    <div {...rest} className={containerClass}>
      {children}
    </div>
  );
};

export type HeaderGroupContentAlignment =
  | 'top'
  | 'nearTop'
  | 'right'
  | 'center'
  | 'bottom'
  | 'nearBottom'
  | 'left'
  | 'centerOfParent';
export interface HeaderGroupProps {
  /**
   * Note: `centerOfParent` only works in conjunction with `autoGrow`. Without it, `centerOfParent`
   * will be treated as `center`.
   */
  alignContent?: HeaderGroupContentAlignment;
  autoGrow?: boolean;
  children?: React.ReactNode;
  className?: string;
  spaceBetweenChildren?: boolean;
  style?: React.CSSProperties;
}

/**
 * Automatically positions the children of the element to be centered in the element's parent,
 * staying within the bounds of the element itself.
 * @param element Ref to the `HeaderGroup` element.
 * @param enabled Whether auto positioning should be enabled.
 */
const useCenterOfParentAligmnent = <T extends HTMLElement>(element: T | null, enabled: boolean) => {
  const [leftPosition, setLeftPosition] = useState('max(0px, 50%)');

  // Calculate the current left offset, and update the tracked offset if it has changed
  const calculateElementBounds = useCallback(() => {
    if (!element?.parentElement || !enabled) return;

    const contentWidth =
      element.querySelector<HTMLElement>(`.${styles.alignToCenterSpacer}`)?.offsetWidth ?? 0;

    const offsetLeft = element.offsetLeft + contentWidth / 2;

    const offsetRight =
      (element.nextElementSibling instanceof HTMLElement
        ? element.nextElementSibling.offsetLeft
        : element.parentElement.offsetWidth) - contentWidth;

    setLeftPosition(`clamp(${offsetLeft}px, 50%, ${Math.max(offsetRight, offsetLeft)}px)`);
  }, [element, enabled]);

  const resizeObserver = useMemo(
    () => new ResizeObserver(calculateElementBounds),
    [calculateElementBounds]
  );
  const mutationObserver = useMemo(
    () => new MutationObserver(calculateElementBounds),
    [calculateElementBounds]
  );

  // Calculate the initial offset and observe for parent size changes that could affect it
  useEffect(() => {
    if (!element?.parentElement || !enabled) return;

    const parentPosition = getComputedStyle(element.parentElement).position;
    if (parentPosition !== 'relative') {
      debug.warn(
        "useCenterOfParentAligmnent target element's parent has `position: %s`, but `position: relative` is required: %o",
        parentPosition,
        element.parentElement
      );
    }

    calculateElementBounds();
    resizeObserver.observe(element.parentElement);
    mutationObserver.observe(element.parentElement, { childList: true, subtree: true });
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [element, enabled, calculateElementBounds, resizeObserver, mutationObserver]);

  // Return the CSS styles for the wrapper element
  return useMemo((): CSSProperties => {
    if (!element || !enabled) return {};

    return {
      left: leftPosition,
      position: 'absolute',
      transform: 'translateX(-50%)',
      whiteSpace: 'nowrap',
    };
  }, [element, enabled, leftPosition]);
};

export const HeaderGroup: React.FC<HeaderGroupProps> = props => {
  const {
    children,
    className,
    autoGrow,
    spaceBetweenChildren,
    alignContent = 'left',
    ...rest
  } = props;

  const validAlignContent =
    alignContent === 'centerOfParent' && !autoGrow ? 'center' : alignContent;

  const [element, setElement] = useState<HTMLDivElement | null>(null);

  const containerClass = classNames(
    styles.group,
    styles[`align-${validAlignContent}`],
    {
      [styles.autoGrow]: autoGrow,
      [styles.spaceBetweenChildren]: spaceBetweenChildren,
    },
    className
  );

  const alignContentToParentCenter = validAlignContent === 'centerOfParent';
  const wrapperStyle = useCenterOfParentAligmnent(element, alignContentToParentCenter);

  return (
    <div {...rest} className={containerClass} ref={setElement}>
      {alignContentToParentCenter ? (
        <>
          <div style={wrapperStyle}>{children}</div>
          {/*
            Since the wrapper element will be absolutely positioned, we render a hidden clone to
            take up the same minimum space, so it doesn't clip behind adjacent sibling elements to
            the right.
          */}
          <div className={styles.alignToCenterSpacer}>
            {React.Children.map(children, child =>
              React.isValidElement(child) ? React.cloneElement(child) : child
            )}
          </div>
        </>
      ) : (
        children
      )}
    </div>
  );
};

// Maybe modifier on HeaderGroup or styled from components that need it?
export const HeaderGroupText: React.FC<HeaderGroupProps> = props => {
  const { children, alignContent, ...rest } = props;
  const textClass = classNames(styles.groupText, styles[`align-${alignContent}`]);
  return (
    <HeaderGroup {...rest}>
      <div className={textClass}>{children}</div>
    </HeaderGroup>
  );
};

export interface HeaderRowProps extends React.HTMLAttributes<HTMLDivElement> {
  alignContent?: HeaderGroupContentAlignment;
  autoGrow?: boolean;
  children?: React.ReactNode;
  className?: string;
}

export const HeaderRow: React.FC<HeaderRowProps> = props => {
  const { children, className, autoGrow, alignContent = 'left', ...rest } = props;
  const containerClass = classNames(
    styles.row,
    styles[`align-${alignContent}`],
    {
      [styles.autoGrow]: autoGrow,
    },
    className
  );
  return (
    <div {...rest} className={containerClass}>
      {children}
    </div>
  );
};

interface HeaderRowDividerProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

export const HeaderRowDivider: React.FC<HeaderRowDividerProps> = ({ className, ...rest }) => (
  <div className={classNames(styles.headerRowDivider, className)} {...rest} />
);
